import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button, Input } from "../components/ui";

export default function Login() {
  const { admin, loading, loginWithPasskey, loginWithTotp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"passkey" | "totp">("passkey");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";

  if (!loading && admin) return <Navigate to={from} replace />;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "passkey") {
        await loginWithPasskey(email.trim());
      } else {
        await loginWithTotp(email.trim(), code.trim());
      }
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Browser aborts (user dismissed the passkey prompt) read badly raw.
      setError(
        message.includes("timed out or was not allowed")
          ? "Passkey prompt was dismissed. Try again, or use your backup code."
          : message,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-rail-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center text-white">
          <h1 className="text-xl font-bold">RailAssurance Admin</h1>
          <p className="mt-1 text-sm text-rail-300">Restricted access</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 rounded-lg bg-white p-6 shadow-xl">
          <div>
            <label htmlFor="email" className="mb-1 block text-xs font-medium text-slate-600">
              Email
            </label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="username webauthn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@railassurance.co.uk"
            />
          </div>
          {mode === "totp" && (
            <div>
              <label htmlFor="code" className="mb-1 block text-xs font-medium text-slate-600">
                Backup code (TOTP)
              </label>
              <Input
                id="code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" disabled={busy || !email} className="w-full">
            {busy ? "Signing in…" : mode === "passkey" ? "Sign in with passkey" : "Sign in with backup code"}
          </Button>
          <button
            type="button"
            className="w-full text-center text-xs text-rail-600 hover:underline"
            onClick={() => {
              setMode(mode === "passkey" ? "totp" : "passkey");
              setError(null);
            }}
          >
            {mode === "passkey" ? "Passkey unavailable? Use backup code" : "Back to passkey sign-in"}
          </button>
        </form>
      </div>
    </div>
  );
}
